import { useState, useEffect } from 'react';
import {
  getGuardians,
  getMembers,
  createMember,
  linkGuardian,
  unlinkGuardian,
  Guardian,
  Member,
} from '../api/client';

/**
 * Guardian editor, links a player (child Member) to guardian Member(s)
 * via the player_guardians junction. Shown inside the player edit modal
 * when features.members is on. The player's consent block (the
 * safeguarding artifact) stays separate and untouched.
 *
 * Add flow: search existing members by name/email, OR create a brand-new
 * guardian inline (name + optional email) which becomes a Member with the
 * guardian + parent roles and is linked in one step.
 */

interface Props {
  playerId: string;
}

export default function GuardianEditor({ playerId }: Props) {
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'primary' | 'secondary'>('primary');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [g, m] = await Promise.all([getGuardians(playerId), getMembers()]);
      setGuardians(g);
      setMembers(m);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load guardians');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [playerId]);

  const linkedIds = new Set(guardians.map(g => g.memberId));
  const term = search.trim().toLowerCase();
  const candidates = term
    ? members
        .filter(m => m.status === 'active' && !linkedIds.has(m.memberId))
        .filter(m => `${m.name} ${m.email}`.toLowerCase().includes(term))
        .slice(0, 6)
    : [];

  async function linkExisting(memberId: string) {
    setBusy(true);
    setError('');
    try {
      await linkGuardian(playerId, memberId, role);
      setSearch('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to link guardian');
    } finally {
      setBusy(false);
    }
  }

  async function createAndLink() {
    if (!newName.trim()) { setError('Guardian name is required'); return; }
    setBusy(true);
    setError('');
    try {
      const m = await createMember({
        name: newName.trim(),
        email: newEmail.trim() || undefined,
        ageType: 'adult',
        roles: ['guardian'],
      });
      await linkGuardian(playerId, m.memberId, role);
      setNewName('');
      setNewEmail('');
      setShowCreate(false);
      setSearch('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create guardian');
    } finally {
      setBusy(false);
    }
  }

  async function remove(memberId: string) {
    setBusy(true);
    setError('');
    try {
      await unlinkGuardian(playerId, memberId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove guardian');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="guardian-editor">
      {error && <div className="ealert ealert-error" style={{ marginBottom: 10 }}>{error}</div>}

      {loading ? (
        <p className="tm-muted">Loading guardians…</p>
      ) : guardians.length === 0 ? (
        <p className="tm-muted" style={{ marginBottom: 10 }}>No guardians linked yet.</p>
      ) : (
        <ul className="guardian-list">
          {guardians.map(g => (
            <li key={g.memberId} className="guardian-row">
              <div>
                <span className="tm-person-name">{g.name || '(no name)'}</span>
                {' '}
                <span className="tm-chip">{g.role}</span>
                {g.email && (
                  <a className="tm-link" href={`mailto:${g.email}`} style={{ marginLeft: 8 }}>{g.email}</a>
                )}
              </div>
              <button
                type="button"
                className="ebtn ebtn-danger ebtn-sm"
                disabled={busy}
                onClick={() => remove(g.memberId)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Add guardian */}
      <div className="guardian-add">
        <div className="tm-form-row" style={{ alignItems: 'flex-end' }}>
          <label className="tm-field" style={{ marginBottom: 0 }}>
            <span>Add a guardian</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search members by name or email…"
              disabled={busy}
            />
          </label>
          <label className="tm-field" style={{ marginBottom: 0, maxWidth: 140 }}>
            <span>Role</span>
            <select value={role} onChange={e => setRole(e.target.value as 'primary' | 'secondary')} disabled={busy}>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </select>
          </label>
        </div>

        {term && candidates.length > 0 && (
          <ul className="guardian-candidates">
            {candidates.map(m => (
              <li key={m.memberId}>
                <button type="button" className="tm-link" disabled={busy} onClick={() => linkExisting(m.memberId)}>
                  {m.name}{m.email ? ` · ${m.email}` : ''}
                </button>
              </li>
            ))}
          </ul>
        )}

        {term && candidates.length === 0 && (
          <p className="tm-muted" style={{ fontSize: '0.8125rem', margin: '6px 0' }}>
            No matching member. {' '}
            <button type="button" className="tm-link" onClick={() => { setShowCreate(true); setNewName(search.trim()); }}>
              Create “{search.trim()}” as a new guardian
            </button>
          </p>
        )}

        {!showCreate ? (
          <button type="button" className="ebtn ebtn-secondary ebtn-sm" style={{ marginTop: 8 }} onClick={() => setShowCreate(true)}>
            + New guardian
          </button>
        ) : (
          <div className="guardian-create">
            <div className="tm-form-row">
              <label className="tm-field">
                <span>Name *</span>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} disabled={busy} />
              </label>
              <label className="tm-field">
                <span>Email (optional)</span>
                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} disabled={busy} />
              </label>
            </div>
            <div className="tm-actions">
              <button type="button" className="ebtn ebtn-secondary ebtn-sm" disabled={busy} onClick={() => { setShowCreate(false); setNewName(''); setNewEmail(''); }}>
                Cancel
              </button>
              <button type="button" className="ebtn ebtn-primary ebtn-sm" disabled={busy} onClick={createAndLink}>
                {busy ? 'Saving…' : 'Add guardian'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
